/** Sección "Costos de ejecución" del presupuesto SA — insumos manuales, mano de obra multilínea,
 * parámetros editables por presupuesto (solo admin) y snapshot histórico. */
import React, { useState, useEffect } from 'react';
import api from '@/api/axios';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Plus, X, FloppyDisk, ArrowsClockwise } from '@phosphor-icons/react';

const Q = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const errMsg = (e, def) => e?.response?.data?.detail || def;

const CAMPOS = [
  ['km', 'Km desde sede'], ['vehiculos', 'Vehículos'], ['personas', 'Personas'],
  ['dias_trabajo', 'Días de trabajo'], ['noches_hotel', 'Noches de hotel'],
  ['dias_alimentacion', 'Días alimentación'], ['peajes_monto', 'Peajes (Q)'], ['viajes_peaje', 'Viajes (peaje)'],
];
const PARAMS_EDIT = [
  ['costo_km', 'Costo logístico Q/km'], ['depreciacion_km', 'Depreciación Q/km'],
  ['hotel_persona_noche', 'Hotel Q/persona/noche'], ['alimentacion_persona_dia', 'Alimentación Q/persona/día'],
  ['mano_obra_persona_dia', 'M.O. Q/persona/día'], ['mano_obra_m2_vidrio', 'M.O. vidrio Q/m²'],
];
const TIPOS_MO = [
  ['dia_persona', 'Por día/persona'], ['m2', 'Por m²'], ['unidad', 'Por unidad'], ['hora', 'Por hora'], ['fijo', 'Monto fijo'],
];

const moSubtotal = (l) => {
  const cu = Number(l.costo_unitario || 0);
  if (l.tipo_calculo === 'dia_persona') return Number(l.personas || 0) * Number(l.dias || 0) * cu;
  if (l.tipo_calculo === 'fijo') return cu;
  return Number(l.cantidad || 0) * cu;
};

export const EjecucionCard = ({ proyecto, adminLike, bloqueado, onSaved }) => {
  const ej = proyecto.ejecucion || {};
  const [f, setF] = useState({
    km: ej.km ?? '', ida_y_vuelta: ej.ida_y_vuelta !== false, vehiculos: ej.vehiculos ?? 1,
    personas: ej.personas ?? '', dias_trabajo: ej.dias_trabajo ?? '', noches_hotel: ej.noches_hotel ?? '',
    dias_alimentacion: ej.dias_alimentacion ?? '', peajes_monto: ej.peajes_monto ?? '', viajes_peaje: ej.viajes_peaje ?? '',
    mano_obra: (ej.mano_obra || []).map((l) => ({ ...l })),
    otros: (ej.otros || []).map((o) => ({ ...o })),
    mo_metodo: ej.mo_metodo || '', mo_costo_dia: ej.mo_costo_dia || '', mo_m2: ej.mo_m2 || '', mo_costo_m2: ej.mo_costo_m2 || '',
  });
  const [par, setPar] = useState(ej.parametros ? { ...ej.parametros } : null);
  useEffect(() => {
    if (adminLike && !ej.parametros) {
      api.get('/sa/config-ejecucion').then((r) => setPar(r.data)).catch(() => {});
    }
  }, [adminLike, ej.parametros]);
  const [saving, setSaving] = useState(false);
  const det = ej.detalle || null;

  const guardar = async () => {
    setSaving(true);
    try {
      const body = {
        km: Number(f.km || 0), ida_y_vuelta: !!f.ida_y_vuelta, vehiculos: Number(f.vehiculos || 1),
        personas: Number(f.personas || 0), dias_trabajo: Number(f.dias_trabajo || 0),
        noches_hotel: Number(f.noches_hotel || 0), dias_alimentacion: Number(f.dias_alimentacion || 0),
        peajes_monto: Number(f.peajes_monto || 0), viajes_peaje: Number(f.viajes_peaje || 0),
        mano_obra: f.mano_obra.filter((l) => l.descripcion || Number(l.costo_unitario)).map((l) => ({
          descripcion: l.descripcion || '', tipo_calculo: l.tipo_calculo || 'dia_persona',
          personas: Number(l.personas || 0), dias: Number(l.dias || 0), cantidad: Number(l.cantidad || 0),
          unidad: l.unidad || '', costo_unitario: Number(l.costo_unitario || 0),
        })),
        otros: f.otros.filter((o) => o.descripcion).map((o) => ({
          descripcion: o.descripcion, cantidad: Number(o.cantidad || 0), unidad: o.unidad || 'unidad',
          costo_unitario: Number(o.costo_unitario || 0), observaciones: o.observaciones || '',
        })),
        mo_metodo: f.mo_metodo || '', mo_costo_dia: Number(f.mo_costo_dia || 0),
        mo_m2: Number(f.mo_m2 || 0), mo_costo_m2: Number(f.mo_costo_m2 || 0),
      };
      if (adminLike && par) {
        body.parametros = {};
        PARAMS_EDIT.forEach(([k]) => { body.parametros[k] = Number(par[k] || 0); });
        if (par.gasolina_km !== undefined) body.parametros.gasolina_km = Number(par.gasolina_km || 0);
      }
      await api.put(`/sa/proyectos/${proyecto.id}/ejecucion`, body);
      toast.success('Costos de ejecución guardados');
      onSaved();
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar')); }
    finally { setSaving(false); }
  };

  const recalcular = async () => {
    if (!window.confirm('Esto reemplazará los valores actuales del presupuesto por los costos vigentes. ¿Desea continuar?')) return;
    try {
      await api.post(`/sa/proyectos/${proyecto.id}/recalcular-costos`);
      toast.success('Presupuesto recalculado con costos vigentes');
      onSaved();
    } catch (e) { toast.error(errMsg(e, 'No se pudo recalcular')); }
  };

  const setLn = (key, i, k, v) => {
    const arr = [...f[key]];
    arr[i] = { ...arr[i], [k]: v };
    setF({ ...f, [key]: arr });
  };

  const nuevaMO = () => setF({
    ...f,
    mano_obra: [...f.mano_obra, { descripcion: '', tipo_calculo: 'dia_persona', personas: '', dias: '', cantidad: '', unidad: '', costo_unitario: par?.mano_obra_persona_dia ?? '' }],
  });
  const setTipoMO = (i, tipo) => {
    const arr = [...f.mano_obra];
    const sugerido = tipo === 'm2' ? (par?.mano_obra_m2_vidrio ?? '') : tipo === 'dia_persona' ? (par?.mano_obra_persona_dia ?? '') : arr[i].costo_unitario;
    arr[i] = { ...arr[i], tipo_calculo: tipo, costo_unitario: sugerido, unidad: tipo === 'm2' ? 'm²' : tipo === 'hora' ? 'hora' : arr[i].unidad };
    setF({ ...f, mano_obra: arr });
  };

  return (
    <Card className="p-4 space-y-3" data-testid="ejecucion-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-black uppercase tracking-wide text-[var(--text-muted)]"><span className="md:hidden text-[#0EA5E9]">Paso 6 — </span>Costos de ejecución</h3>
        {adminLike && (
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={recalcular} data-testid="ej-recalcular">
            <ArrowsClockwise size={14} className="mr-1" /> Recalcular con costos actuales
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {CAMPOS.filter(([k]) => !(f.mo_metodo === 'planilla' && (k === 'personas' || k === 'dias_trabajo'))).map(([k, label]) => (
          <div key={k}>
            <label className="text-xs font-bold text-[var(--text-muted)]">
              {f.mo_metodo === 'trato_m2' && k === 'personas' ? 'Personas (solo hotel/alim.)'
                : f.mo_metodo === 'trato_m2' && k === 'dias_trabajo' ? 'Días (no cuenta en M.O.)' : label}
            </label>
            <Input type="number" step="any" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
              className="h-9" data-testid={`ej-${k}`} />
          </div>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={f.ida_y_vuelta} onChange={(e) => setF({ ...f, ida_y_vuelta: e.target.checked })}
          className="accent-[#0EA5E9] w-4 h-4" data-testid="ej-ida-vuelta" />
        Ida y vuelta (km × 2)
        {det && <span className="text-xs text-[var(--text-muted)]">— Km considerados: <b>{det.km_considerados}</b></span>}
      </label>

      {/* MÉTODO DE MANO DE OBRA — EXCLUYENTE: planilla O por trato, nunca ambos */}
      {adminLike && (
        <div className="border border-[var(--border)] rounded-lg p-2.5 space-y-2" data-testid="ej-mo-metodo">
          <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)]">Método de mano de obra</p>
          <p className="text-[10px] text-[var(--text-muted)]">Los dos métodos son <b>excluyentes</b>: se usa uno u otro, nunca se suman. Planilla = personas × días × Q/persona/día · Por trato = m² × Q/m².</p>
          <div className="flex gap-2 flex-wrap">
            <button type="button" data-testid="ej-mo-metodo-planilla"
              className={`h-8 px-3 text-xs font-bold rounded-md border ${f.mo_metodo === 'planilla' ? 'bg-[#0EA5E9] text-white border-[#0EA5E9]' : 'border-[var(--border)] text-[var(--text)]'}`}
              onClick={() => setF({ ...f, mo_metodo: 'planilla' })}>
              Personal en planilla
            </button>
            <button type="button" data-testid="ej-mo-metodo-trato"
              className={`h-8 px-3 text-xs font-bold rounded-md border ${f.mo_metodo === 'trato_m2' ? 'bg-[#0EA5E9] text-white border-[#0EA5E9]' : 'border-[var(--border)] text-[var(--text)]'}`}
              onClick={() => setF({ ...f, mo_metodo: 'trato_m2' })}>
              Personal fuera de planilla / por trato
            </button>
          </div>
          {f.mo_metodo === 'planilla' && (
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] font-bold text-[var(--text-muted)]">Personas</label>
                <Input type="number" value={f.personas} onChange={(e) => setF({ ...f, personas: e.target.value })} className="h-8 text-xs" data-testid="ej-mo-pl-personas" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-[var(--text-muted)]">Días de trabajo</label>
                <Input type="number" value={f.dias_trabajo} onChange={(e) => setF({ ...f, dias_trabajo: e.target.value })} className="h-8 text-xs" data-testid="ej-mo-pl-dias" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-[var(--text-muted)]">Q/persona/día</label>
                <Input type="number" value={f.mo_costo_dia} placeholder={String(par?.mano_obra_persona_dia ?? 200)}
                  onChange={(e) => setF({ ...f, mo_costo_dia: e.target.value })} className="h-8 text-xs" data-testid="ej-mo-pl-costo" />
              </div>
              <p className="col-span-3 text-[10px] text-[var(--text-muted)]">M.O. = personas × días × Q/día. NO se calcula mano de obra por m².</p>
            </div>
          )}
          {f.mo_metodo === 'trato_m2' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-bold text-[var(--text-muted)]">m² a pagar</label>
                <Input type="number" value={f.mo_m2} onChange={(e) => setF({ ...f, mo_m2: e.target.value })} className="h-8 text-xs" data-testid="ej-mo-tr-m2" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-[var(--text-muted)]">Q/m²</label>
                <Input type="number" value={f.mo_costo_m2} placeholder={String(par?.mano_obra_m2_vidrio ?? 125)}
                  onChange={(e) => setF({ ...f, mo_costo_m2: e.target.value })} className="h-8 text-xs" data-testid="ej-mo-tr-costo" />
              </div>
              <p className="col-span-2 text-[10px] text-[var(--text-muted)]">M.O. = m² × Q/m². NO se calculan personas × días (los campos Personas/Días quedan solo para logística: hotel/alimentación).</p>
            </div>
          )}
          {!f.mo_metodo && f.mano_obra.length > 0 && (
            <p className="text-[10px] text-amber-500">Este presupuesto usa el detalle multilínea histórico. Al elegir un método, la M.O. se calculará SOLO con ese método.</p>
          )}
          {!f.mo_metodo && f.mano_obra.length === 0 && (
            <p className="text-[11px] text-[var(--text-muted)]">Elige un método. Sin selección se usa el modo histórico: Personas × Días × Q{par?.mano_obra_persona_dia ?? 200}/día.</p>
          )}
        </div>
      )}

      {/* Mano de obra multilínea — HISTÓRICO (solo visible si no hay método seleccionado) */}
      {adminLike && !f.mo_metodo && (
        <div>
          <div className="flex items-center justify-between">
            <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)]">Mano de obra (multilínea)</p>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={nuevaMO} data-testid="ej-add-mo">
              <Plus size={13} className="mr-0.5" /> Línea de mano de obra
            </Button>
          </div>
          {f.mano_obra.length === 0 && (
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5">Sin líneas: se usa el modo simple Personas × Días × Q{par?.mano_obra_persona_dia ?? 200}/día.</p>
          )}
          {f.mano_obra.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-1 items-center mt-1.5">
              <Input value={l.descripcion} onChange={(e) => setLn('mano_obra', i, 'descripcion', e.target.value)} placeholder="Descripción (instalación, vidrio…)" className="col-span-3 h-8 text-xs" data-testid={`ej-mo-desc-${i}`} />
              <select value={l.tipo_calculo} onChange={(e) => setTipoMO(i, e.target.value)}
                className="col-span-2 h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1" data-testid={`ej-mo-tipo-${i}`}>
                {TIPOS_MO.map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
              </select>
              {l.tipo_calculo === 'dia_persona' ? (
                <>
                  <Input type="number" value={l.personas} onChange={(e) => setLn('mano_obra', i, 'personas', e.target.value)} placeholder="Pers." className="col-span-2 h-8 text-xs" data-testid={`ej-mo-personas-${i}`} />
                  <Input type="number" value={l.dias} onChange={(e) => setLn('mano_obra', i, 'dias', e.target.value)} placeholder="Días" className="col-span-1 h-8 text-xs" data-testid={`ej-mo-dias-${i}`} />
                </>
              ) : l.tipo_calculo === 'fijo' ? (
                <span className="col-span-3 text-[10px] text-[var(--text-muted)] text-center">monto fijo</span>
              ) : (
                <>
                  <Input type="number" value={l.cantidad} onChange={(e) => setLn('mano_obra', i, 'cantidad', e.target.value)} placeholder="Cant." className="col-span-2 h-8 text-xs" data-testid={`ej-mo-cant-${i}`} />
                  <Input value={l.unidad} onChange={(e) => setLn('mano_obra', i, 'unidad', e.target.value)} placeholder="Unidad" className="col-span-1 h-8 text-xs" />
                </>
              )}
              <Input type="number" value={l.costo_unitario} onChange={(e) => setLn('mano_obra', i, 'costo_unitario', e.target.value)} placeholder="Q unit." className="col-span-2 h-8 text-xs" data-testid={`ej-mo-costo-${i}`} />
              <span className="col-span-1 text-[11px] font-bold text-right" data-testid={`ej-mo-subtotal-${i}`}>{Q(moSubtotal(l))}</span>
              <button type="button" className="col-span-1 text-red-400 flex justify-center" onClick={() => setF({ ...f, mano_obra: f.mano_obra.filter((_, j) => j !== i) })} data-testid={`ej-mo-del-${i}`}>
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Otros costos — montos internos: solo con permiso Costos SA */}
      {adminLike && (
      <div>
        <div className="flex items-center justify-between">
          <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)]">Otros costos</p>
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs"
            onClick={() => setF({ ...f, otros: [...f.otros, { descripcion: '', cantidad: 1, unidad: 'unidad', costo_unitario: '', observaciones: '' }] })}
            data-testid="ej-add-otro">
            <Plus size={13} className="mr-0.5" /> Agregar otro costo
          </Button>
        </div>
        {f.otros.map((o, i) => (
          <div key={i} className="grid grid-cols-12 gap-1 items-center mt-1.5">
            <Input value={o.descripcion} onChange={(e) => setLn('otros', i, 'descripcion', e.target.value)} placeholder="Descripción (andamio, grúa…)" className="col-span-4 h-8 text-xs" data-testid={`ej-otro-desc-${i}`} />
            <Input type="number" value={o.cantidad} onChange={(e) => setLn('otros', i, 'cantidad', e.target.value)} placeholder="Cant." className="col-span-2 h-8 text-xs" data-testid={`ej-otro-cant-${i}`} />
            <Input value={o.unidad} onChange={(e) => setLn('otros', i, 'unidad', e.target.value)} placeholder="Unidad" className="col-span-2 h-8 text-xs" />
            <Input type="number" value={o.costo_unitario} onChange={(e) => setLn('otros', i, 'costo_unitario', e.target.value)} placeholder="Costo Q" className="col-span-2 h-8 text-xs" data-testid={`ej-otro-costo-${i}`} />
            <Input value={o.observaciones} onChange={(e) => setLn('otros', i, 'observaciones', e.target.value)} placeholder="Obs." className="col-span-1 h-8 text-xs" />
            <button type="button" className="col-span-1 text-red-400 flex justify-center" onClick={() => setF({ ...f, otros: f.otros.filter((_, j) => j !== i) })} data-testid={`ej-otro-del-${i}`}>
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
      )}

      {/* Parámetros de ESTE presupuesto — editables por administración */}
      {adminLike && par && (
        <div className="border border-[var(--border)] rounded-lg p-2.5 bg-[var(--bg)]" data-testid="ej-parametros">
          <p className="text-[10px] font-bold uppercase text-amber-500 mb-1.5">Valores usados en este presupuesto (editables — no cambian la configuración estándar)</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PARAMS_EDIT.map(([k, label]) => (
              <div key={k}>
                <label className="text-[10px] font-bold text-[var(--text-muted)]">{label}</label>
                <Input type="number" step="any" value={par[k] ?? ''} onChange={(e) => setPar({ ...par, [k]: e.target.value })}
                  className="h-8 text-xs" data-testid={`ej-par-${k}`} />
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[var(--text-muted)] mt-1.5">⛽ Gasolina: fórmula pendiente de configuración — por ahora solo se registran los kilómetros ({det ? det.km_considerados : 0} km considerados).</p>
        </div>
      )}

      {/* Desglose calculado — solo administración */}
      {adminLike && det && (
        <div className="border border-[var(--border)] rounded-lg p-2.5 bg-[var(--bg)] text-xs space-y-0.5" data-testid="ej-desglose">
          <p className="text-[10px] font-bold uppercase text-amber-500 mb-1">Desglose interno (snapshot)</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-0.5">
            <span>Logística (km): <b>{Q(det.transporte)}</b></span>
            <span>Depreciación: <b>{Q(det.depreciacion)}</b></span>
            <span>Gasolina: <b>{det.gasolina ? Q(det.gasolina) : 'pendiente'}</b></span>
            <span>Mano de obra: <b>{Q(det.mano_obra)}</b></span>
            <span className="col-span-2 sm:col-span-4 text-[10px] text-amber-500" data-testid="ej-mo-metodo-label">Método de M.O.: <b>{det.mo_metodo_label || 'Personal en planilla'}</b></span>
            <span>Hotel: <b>{Q(det.hotel)}</b></span>
            <span>Alimentación: <b>{Q(det.alimentacion)}</b></span>
            <span>Peajes: <b>{Q(det.peajes)}</b></span>
            <span>Otros: <b>{Q(det.otros)}</b></span>
          </div>
          <p className="pt-1 font-bold">Total ejecución: <span className="text-[#0EA5E9]" data-testid="ej-total">{Q(det.total)}</span></p>
          {det.aviso_mo && <p className="text-[10px] text-red-400 font-bold" data-testid="ej-aviso-mo">{det.aviso_mo}</p>}
        </div>
      )}

      <Button size="sm" onClick={guardar} disabled={saving || bloqueado} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="ej-guardar">
        <FloppyDisk size={15} className="mr-1" /> {saving ? 'Guardando…' : 'Guardar costos de ejecución'}
      </Button>
    </Card>
  );
};
